import { useEffect, useState, useContext, useCallback } from 'react'
import { VendorContext } from '../../globalState/Vendor/index'
import {
  Card,
  Layout,
  useToaster,
  Spinner,
  Grid,
} from '@enterprise-ui/canvas-ui-react'
import { useNavigate, useParams } from 'react-router'
import {
  TOASTER_DEFAULTS,
  COST_SEARCH_TIMEFRAME_OPTIONS,
  COST_SEARCH_VIEW_BY_OPTIONS,
  COST_SEARCH_DEFAULT_SELECTIONS,
  trainingLinkUrl,
} from '../../globalConfig/constants'

import { SectionHeader } from '../../globalComponents/SectionHeader'
import {
  deleteSaveView,
  downloadSearchedCosts,
  getSavedView,
  getSavedViewsMetadata,
  saveView,
  searchCosts,
  updateSaveView,
} from '../../services/requests'
import { CostSearch } from '../CostSearch/CostSearch'
import uniq from 'lodash/uniq'
import { DataTable } from '../../globalComponents/DataTable'
import _ from 'lodash'
import { SaveViewModal } from './SaveViewModal'
import fileDownload from 'js-file-download'
import { SavedViewContext } from '../../globalState/SavedViews'

export const SavedView = () => {
  const navigate = useNavigate()
  const makeToast = useToaster()
  const { savedViewId } = useParams()
  const [usersSavedView, setUsersSavedView] = useState(null)
  const [searchSelections, setSearchSelections] = useState(null)
  const [currentlySelectedCriteria, setCurrentlySelectedCriteria] =
    useState(null)
  const [searchResults, setSearchResults] = useState(null)
  const [waitingOnSavedView, setWaitingOnSavedView] = useState(true)
  const [waitingOnSaveViewModal, setWaitingOnSaveViewModal] = useState(false)
  const [waitingOnSearchResults, setWaitingOnSearchResults] = useState(false)
  const [isSearchExpanded, setIsSearchExpanded] = useState(false)
  const [renderSavePopup, setRenderSavePopup] = useState(false)
  const [renderDeletePopup, setRenderDeletePopup] = useState(false)
  const [waitingOnDownload, setWaitingOnDownload] = useState(false)
  const [state] = useContext(VendorContext)
  // eslint-disable-next-line no-unused-vars
  const [savedViewState, savedViewDispatch] = useContext(SavedViewContext)

  const makeCostSearchRequest = useCallback(
    (vendor, selections) => {
      const sendFailureToast = () => {
        makeToast({
          ...TOASTER_DEFAULTS,
          type: 'error',
          heading: 'Error',
          message: 'Failed to get search results',
        })
      }

      setWaitingOnSearchResults(true)
      const requestBody = { ...selections, vendorId: parseInt(vendor) }
      setCurrentlySelectedCriteria(requestBody)
      searchCosts(vendor, requestBody)
        .then((response) => {
          setWaitingOnSearchResults(false)
          if (response?.status === 200) {
            setSearchResults({ ...response?.data, selections: selections })
          } else {
            sendFailureToast()
          }
        })
        .catch(() => {
          setWaitingOnSearchResults(false)
          sendFailureToast()
        })
    },
    [makeToast],
  )

  const handleSaveView = (saveViewName) => {
    const sendFailureToast = () => {
      makeToast({
        ...TOASTER_DEFAULTS,
        type: 'error',
        heading: 'Error',
        message: 'Failed to save view',
      })
    }

    let savedViewForm = {
      viewId: null,
      userId: null,
      title: saveViewName,
      vendorId: parseInt(state?.selectedVendor),
      searchRequest: currentlySelectedCriteria,
    }

    setWaitingOnSaveViewModal(true)
    saveView(parseInt(state?.selectedVendor), savedViewForm)
      .then((response) => {
        setRenderSavePopup(false)
        setWaitingOnSaveViewModal(false)
        if (response?.status === 200) {
          makeToast({
            ...TOASTER_DEFAULTS,
            type: 'success',
            heading: 'Success',
            message:
              'The saved view "' +
              saveViewName +
              '" has been successfuly saved.',
          })
          navigate('/saved-view/' + response?.data?.viewId)
        } else {
          sendFailureToast()
        }
      })
      .catch(() => {
        setRenderSavePopup(false)
        setWaitingOnSaveViewModal(false)
        sendFailureToast()
      })
      .finally(() => {
        getSavedViewMetadata()
      })
  }

  const handleUpdateView = (saveViewName) => {
    const sendFailureToast = () => {
      makeToast({
        ...TOASTER_DEFAULTS,
        type: 'error',
        heading: 'Error',
        message: 'Failed to update view',
      })
    }

    let savedViewForm = {
      viewId: usersSavedView.viewId,
      userId: usersSavedView.userId,
      title: saveViewName,
      vendorId: usersSavedView.vendorId,
      searchRequest: currentlySelectedCriteria,
    }

    setWaitingOnSaveViewModal(true)
    updateSaveView(savedViewId, state?.selectedVendor, savedViewForm)
      .then((response) => {
        setRenderSavePopup(false)
        setWaitingOnSaveViewModal(false)
        if (response?.status === 200) {
          setUsersSavedView(response?.data)
          makeToast({
            ...TOASTER_DEFAULTS,
            type: 'success',
            heading: 'Success',
            message:
              'The saved view "' +
              usersSavedView.title +
              '" has been successfuly updated.',
          })
        } else {
          sendFailureToast()
        }
      })
      .catch(() => {
        setRenderSavePopup(false)
        setWaitingOnSaveViewModal(false)
        sendFailureToast()
      })
  }

  const handleDeleteView = (saveViewName) => {
    const sendFailureToast = () => {
      makeToast({
        ...TOASTER_DEFAULTS,
        type: 'error',
        heading: 'Error',
        message: 'Failed to delete view',
      })
    }

    setWaitingOnSaveViewModal(true)
    deleteSaveView(savedViewId, parseInt(state?.selectedVendor))
      .then((response) => {
        setRenderSavePopup(false)
        setWaitingOnSaveViewModal(false)
        if (response?.status === 200) {
          makeToast({
            ...TOASTER_DEFAULTS,
            type: 'success',
            heading: 'Success',
            message:
              'The saved view "' +
              saveViewName +
              '" has been successfully deleted.',
          })
          navigate('/cost-search')
        } else {
          sendFailureToast()
        }
      })
      .catch(() => {
        setRenderSavePopup(false)
        setWaitingOnSaveViewModal(false)
        sendFailureToast()
      })
      .finally(() => {
        getSavedViewMetadata()
      })
  }

  const getSavedViewMetadata = () => {
    getSavedViewsMetadata(parseInt(state?.selectedVendor))
      .then((response) => {
        if (response?.status === 200) {
          savedViewDispatch({
            type: 'update_saved_views',
            savedViews: response?.data,
          })
        }
      })
      .catch(() => {
        makeToast({
          ...TOASTER_DEFAULTS,
          type: 'error',
          heading: 'Error',
          message: 'Failed to retrieve Saved Views',
        })
      })
  }

  const makeDownloadRequest = () => {
    setWaitingOnDownload(true)
    downloadSearchedCosts(
      searchResults?.selections?.vendorId,
      searchResults?.selections,
    )
      .then((response) => {
        setWaitingOnDownload(false)
        const fileName = `${
          usersSavedView?.title
        } ${new Date().toLocaleString()}.xlsx`
          .replaceAll('/', '-')
          .replaceAll(':', '_')
        fileDownload(response.data, fileName)
        makeToast({
          ...TOASTER_DEFAULTS,
          type: 'success',
          heading: 'Download Successful',
          message: `"${fileName}" successfully downloaded`,
        })
      })
      .catch(() => {
        setWaitingOnDownload(false)
        makeToast({
          ...TOASTER_DEFAULTS,
          type: 'error',
          heading: 'Error',
          message: 'Failed to download search results',
        })
      })
  }

  useEffect(() => {
    setUsersSavedView(null)
    setSearchResults(null)
    setWaitingOnSavedView(true)
    getSavedView(savedViewId)
      .then((response) => {
        setWaitingOnSavedView(false)
        if (
          response?.status === 200 &&
          response?.data?.vendorId === parseInt(state?.selectedVendor)
        ) {
          setUsersSavedView(response?.data)
          setSearchSelections(response?.data?.searchRequest)
        }
      })
      .catch(() => {
        setWaitingOnSavedView(false)
        makeToast({
          ...TOASTER_DEFAULTS,
          type: 'error',
          heading: 'Error',
          message: 'Failed to retrieve Saved View.',
        })
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.selectedVendor, savedViewId])

  useEffect(() => {
    if (
      usersSavedView &&
      !(usersSavedView?.searchRequest.timeframe === 'CURRENT')
    ) {
      if (
        new Date() >
        new Date(
          usersSavedView?.searchRequest[
            COST_SEARCH_TIMEFRAME_OPTIONS.START_DATE.variableName
          ],
        )
      ) {
        makeToast({
          ...TOASTER_DEFAULTS,
          type: 'error',
          heading: 'Error',
          message:
            'The timeframe of this saved view is now in the past. Please select a valid date to proceed.',
        })
        setIsSearchExpanded(true)
      } else {
        makeCostSearchRequest(state.selectedVendor, searchSelections)
      }
    } else if (usersSavedView) {
      makeCostSearchRequest(state.selectedVendor, searchSelections)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersSavedView])

  useEffect(() => {
    if (searchResults) {
      setSearchResults(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedVendor])

  const setNewSearchSelections = (newSearchSelectionValues) => {
    const concatinatedArrays = {}
    Object.keys(newSearchSelectionValues).forEach((selectionKey) => {
      concatinatedArrays[selectionKey] = Array.isArray(
        searchSelections[selectionKey],
      )
        ? uniq(
            searchSelections[selectionKey].concat(
              newSearchSelectionValues[selectionKey],
            ),
            false,
          )
        : newSearchSelectionValues[selectionKey]
    })
    setSearchSelections((prevSearchSelections) => {
      return {
        ...prevSearchSelections,
        ...concatinatedArrays,
      }
    })
  }

  const removeSelection = useCallback((item, field, values, type) => {
    var tempList
    if (type) {
      tempList = []
    } else {
      tempList = values
      var removeObj = tempList.findIndex((obj) => obj === item)
      tempList.splice(removeObj, 1)
    }

    if (
      Object.values(COST_SEARCH_VIEW_BY_OPTIONS).find(
        (obj) => obj.variableName === field,
      )
    ) {
      setSearchSelections((prevState) => {
        return {
          ...prevState,
          costLocationGroup: {
            costLocationGroupType: field,
            costLocationGroupValues: tempList,
          },
        }
      })
    } else {
      setSearchSelections((prevState) => {
        return {
          ...prevState,
          [field]: tempList,
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSearchClick = () => {
    makeCostSearchRequest(state.selectedVendor, searchSelections)
  }

  const handleResetSelectionsClick = () => {
    setSearchSelections(COST_SEARCH_DEFAULT_SELECTIONS)
  }

  const handleSaveViewOpen = () => {
    setRenderSavePopup(true)
  }

  const handleDeleteViewOpen = () => {
    setRenderDeletePopup(true)
  }

  const onDeleteViewClose = () => {
    setRenderDeletePopup(false)
  }

  const onSaveViewClose = () => {
    setRenderSavePopup(false)
  }

  const headerSubtext = (
    <>
      <span>
        {`Use this page to search and update a vendor's cost records.
        Note that any submitted updates will not be effective until they are approved by Target.
        Visit our `}
      </span>
      <a
        href={trainingLinkUrl}
        target="_blank"
        className="hesp-link"
        rel="noreferrer"
      >
        training materials
      </a>
      <span>
        {` to learn more about how to most effectively use this page.`}
      </span>
    </>
  )

  if (waitingOnSavedView) {
    return (
      <>
        <SectionHeader
          className="hesp-sectionHeader-layout"
          pageHeading="Saved View"
        />
        <Layout.Body includeRail>
          <Grid.Container style={{ height: '400px' }}>
            <Grid.Item className="hesp-general-fullwidth hesp-general-flex">
              <Spinner
                data-testid="saved-view-loading"
                className="hesp-general-marginAuto hesp-general-justifyCenter"
                size="expanded"
              />
            </Grid.Item>
          </Grid.Container>
        </Layout.Body>
      </>
    )
  } else {
    if (usersSavedView) {
      return (
        <>
          <SectionHeader
            className="hesp-sectionHeader-layout"
            pageHeading={usersSavedView.title}
            subtext={headerSubtext}
          />
          <CostSearch
            premadeSelections={usersSavedView.searchRequest}
            addNewSelections={setNewSearchSelections}
            onSearchClick={handleSearchClick}
            onResetSelectionsClick={handleResetSelectionsClick}
            activeSearchSelections={searchSelections}
            vendorId={state.selectedVendor}
            removeValue={removeSelection}
            loading={waitingOnSearchResults}
            isExpanded={isSearchExpanded}
          />
          <DataTable
            tableData={searchResults}
            loading={waitingOnSearchResults}
            onDownload={makeDownloadRequest}
            waitingOnDownload={waitingOnDownload}
            onSaveViewOpen={handleSaveViewOpen}
            onSaveViewDelete={handleDeleteViewOpen}
            disableSaveButton={_.isEqual(
              currentlySelectedCriteria?.startDate === undefined
                ? _.omit(currentlySelectedCriteria, ['startDate', 'endDate'])
                : currentlySelectedCriteria,
              usersSavedView?.searchRequest,
            )}
          />
          <SaveViewModal
            renderPopup={renderDeletePopup}
            modalOnClick={handleDeleteView}
            waitingOnSaveViewModal={waitingOnSaveViewModal}
            onClose={onDeleteViewClose}
            saveViewName={usersSavedView.title}
            modalType="Delete"
          />
          <SaveViewModal
            renderPopup={renderSavePopup}
            modalOnClick={handleUpdateView}
            modalOnClickSaveAs={handleSaveView}
            waitingOnSaveViewModal={waitingOnSaveViewModal}
            onClose={onSaveViewClose}
            saveViewName={usersSavedView.title}
            modalType="Update"
          />
        </>
      )
    } else {
      return (
        <>
          <SectionHeader
            className="hesp-sectionHeader-layout"
            pageHeading="Saved View"
          />
          <Layout.Body
            className="hesp-generic-layout"
            data-testid="savedViewMainContent"
            includeRail
          >
            <Card className="hc-pa-normal hesp-card-border hesp-savedview-card">
              <p className="hc-fs-xl">
                We couldn't find the requested view, make sure you have the
                correct vendor selected in the header above.
              </p>
            </Card>
          </Layout.Body>
        </>
      )
    }
  }
}
